import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { contractsService, fleetService, maintenanceService } from '../../services/firestore';
import { dbService } from '../../services/dbService';
import { auth } from '../../firebase';
import logger from '../../utils/logger';
import { normalizeForSearch } from './utils/searchKey';
import { contactsService } from '../../services/contactsService';
import { CONTRACT_CHANGED } from '../../services/contractVersion';

import { parseSheetDate } from './utils/dates';
import { syncMissingRowsForOpenContracts } from './utils/contractsSync';
import { syncOpenMonthsToStore } from './utils/storeAutoSync';
import { isInvygoContract, contractCategory, determineContractType, matchesContractType, splitSearchResults, freshRowOver } from './utils/contractRows';
import { liveBalance } from './utils/liveBalance';
import { fleetExpiry } from './utils/carExpiry';
import { switchBackLookups } from './workers/contractsStats';
import { inMismatch, switchBackOf } from './utils/switchBack';
import { dubaiToday, contractStatusOf, isOpenStatus } from '../../services/contractRules';
import { USE_CONTRACTS_V2 } from '../../services/contractsV2';
import { contractSourceOf } from './utils/contractSource';

// (kept importable from here: ContractsTable, ContractsManagement, ContractsStatistics and useFleetUsage use them;
// contractCategory is one rule for every page since CON-10 - it lives in utils/contractRows with the worker's copy)
export { isInvygoContract, contractCategory, determineContractType };

/**
 * Rows without a phone (contracts created by the hourly EJAR import, or read
 * from an older IndexedDB copy) get the customer's phone from the contacts store.
 */
// Phone search that survives the way numbers are written ("+966 50…", "00971…", "05…",
// "5…", or just a part): both sides reduced to significant digits (country code and
// leading zeros dropped); 4+ digits typed match anywhere; typed WITH a country code the
// code must match too. Checks the contract's phone and the contacts-store phone.
const fullDigits = (v) => String(v || '').replace(/\D/g, '').replace(/^00/, '');
const sigDigits = (v) => fullDigits(v).replace(/^(971|966)/, '').replace(/^0+/, '');
const phoneMatches = (typed, row) => {
    const t = sigDigits(typed);
    if (t.length < 4) return false;
    const contact = contactsService.peek(row.contractNo) || {};
    const typedFull = fullDigits(typed);
    const withCode = /^(971|966)\d{4,9}$/.test(typedFull);
    return [row.phoneNumber, row['Phone Number'], row.phone, contact.p].some((p) => {
        if (!p) return false;
        if (withCode) return fullDigits(p).includes(typedFull);
        const d = sigDigits(p); return d && d.includes(t);
    });
};
const identityMatches = (term, row) => {
    const contact = contactsService.peek(row.contractNo) || {};
    const own = row.identity || row['Customer ID'] || row.customerId || '';
    return [contact.i, own].some((v) => v && String(v).toLowerCase().replace(/[^a-z0-9]/g, '').includes(term));
};

const withContactPhones = (rows) => (rows || []).map((row) => {
    if (row.phoneNumber || row['Phone Number']) return row;
    const contact = contactsService.peek(row.contractNo || row['Contract No.'] || row.contract_no);
    return contact && contact.p ? { ...row, phoneNumber: contact.p, 'Phone Number': contact.p, phone: contact.p } : row;
});


// Filter Modes
export const FILTER_MODES = {
    ALL: 'all',
    MISMATCH: 'mismatch',
    SWITCHBACK: 'switchback',
    EXPIRED: 'expired',
    BOOKING_EXP: 'booking_exp',
    LATE_PAYMENT: 'late_payment',
    STALE_KM: 'stale_km',
    NO_RENT: 'no_rent',
    UNPAID: 'unpaid'
};

// The tabs whose rows the worker counts (workers/contractsStats), by the key of its answer. 'All Contracts' is the open
// rows themselves: it never waits for the worker (owner, 28 Sep 2026).
const WORKER_LIST_OF_MODE = {
    [FILTER_MODES.MISMATCH]: 'mismatch',
    [FILTER_MODES.SWITCHBACK]: 'switchback',
    [FILTER_MODES.EXPIRED]: 'expired',
    [FILTER_MODES.BOOKING_EXP]: 'bookingExp',
    [FILTER_MODES.LATE_PAYMENT]: 'latePayment',
    [FILTER_MODES.STALE_KM]: 'staleKm',
    [FILTER_MODES.NO_RENT]: 'noRent',
    [FILTER_MODES.UNPAID]: 'unpaid',
};
const NO_WORKER_LISTS = { mismatch: [], switchback: [], expired: [], bookingExp: [], latePayment: [], staleKm: [], noRent: [], unpaid: [] };
// one object for every render: a new {} each time made the memoised table draw all its rows again on every change of
// the page (a tab count arriving, a key typed in the search box) - ~1 s each (owner, 28 Sep 2026)
const NO_DUPLICATES = Object.freeze({});

// The search key (letters kept for contract numbers; phone-like text cleaned as a phone): utils/searchKey



// Removed toDateOnlyString in favor of formatDateDDMMYYYY from ./utils/dates


// --- DATA MAPPING HELPER ---
export const mapFirestoreToLegacy = (doc) => {
    let type = 'open';
    const statusRaw = contractStatusOf(doc);   // a blank status is 'Unknown', never 'Open'
    const statusLower = statusRaw.toLowerCase();
    
    if (statusLower !== 'open') {
        type = doc.Source === 'Invygo' ? 'closed_invygo' : 'closed_other';
        // the Invygo branch / D04 (a typed own car - Edit Mode, a skipped switch back - is any contract's, 4 Nov 2026)
        if (!doc.Source && isInvygoContract(doc)) {
            type = 'closed_invygo';
        }
    }
    
    // Show the exact status: "Open" for an open contract, otherwise the rental system's
    // wording verbatim ("Delivered - Paid", "Delivered - Indebt" ...) — do not lower-case it.
    const status = statusLower === 'open' ? 'Open' : statusRaw;
    // One balance for the column, the badges and the tabs (utils/liveBalance: the Monthly Calculator's rule).
    const netBalance = liveBalance(doc);

    return {
        ...doc,
        'Contract No.': doc.contract_no || doc['Contract No.'] || doc.Agreement,
        'Booking Number': doc.booking_number || doc['Booking Number'],
        'Customer': doc.customer || doc['Customer'],
        'Pick-up Branch': doc.pickup_branch || doc['Pick-up Branch'],
        'Plate No.': doc.plate_no || doc['Plate No.'],
        'Model': doc.model || doc['Model'],
        'Pick-up Date': doc.pickup_date || doc['Pick-up Date'],
        'Drop-off Date': doc.dropoff_date || doc['Drop-off Date'],
        'Phone Number': doc.phone || doc['Phone Number'],
        'Status': status,
        'Invygo Plate': doc.invygo_plate || doc['Invygo Plate'] || '',
        'Invygo Model': doc.invygo_model || doc['Invygo Model'] || '',

        contractNo: doc.contract_no || doc['Contract No.'] || doc.Agreement,
        bookingNumber: doc.booking_number || doc['Booking Number'],
        // who opened it on Ejar, and whether it came from a booking or a walk-in (owner, 5 Oct 2026)
        openedBy: doc.opened_by_short || doc.created_by || doc['Created By'] || '',
        source: contractSourceOf(doc),
        customer: doc.customer || doc['Customer'],
        // Invygo contracts: invygo_plate is the FINGERPRINT (first car on the contract,
        // set by the engine from E-Check or by hand) - never fall back to the current
        // Ejar plate, or a swapped contract would look like it is still on its Invygo car.
        // Any other contract: the car typed by hand, else the car it was opened with (opened_plate - owner, 4 Nov 2026:
        // Switch / Switch Back for every contract), else the car it has now.
        invygoPlate: doc.invygo_plate || doc['Invygo Plate'] || (isInvygoContract(doc) ? '' : (doc.opened_plate || doc.plate_no || doc['Plate No.'])),
        invygoModel: doc.invygo_model || doc['Invygo Model'] || (isInvygoContract(doc) ? '' : (doc.opened_plate ? doc.opened_model : (doc.model || doc['Model']))),
        onYeloCar: !!doc.on_yelo_car,
        carHistory: Array.isArray(doc.car_history) ? doc.car_history : [],
        ejarPlate: doc.plate_no || doc['Plate No.'],
        ejarModel: doc.model || doc['Model'],
        phoneNumber: doc.phone || doc['Phone Number'],
        pickupDate: doc.pickup_date || doc['Pick-up Date'],
        dropoffDate: doc.dropoff_date || doc['Drop-off Date'],
        pickupBranch: doc.pickup_branch || doc['Pick-up Branch'],
        // D04 / Invygo branch shows as "Invygo" everywhere (tables, exports, reports),
        // not the Ejar "Type" column ("Spot") the import stored.
        contractType: isInvygoContract(doc) ? 'Invygo' : (doc.contract_type || doc['Contract Type']),
        type: type,
        netBalance: parseFloat(netBalance.toFixed(2)),
        updateHistory: doc.update_history || doc.updateHistory || [],
        addons: doc.addons || {},
    };
};

/**
 * The contract as it is on the server now, as a page row, over the page's copy (which does not
 * refresh while the page stays open - the engine's Fill from Ejar changes the month rows behind
 * it). The Monthly Calculator opens from this so its Save never writes older rows back (CON-03).
 */
export const freshContractRow = (pageRow) => freshRowOver(pageRow, contractsService.getContractRow,
    (doc) => withContactPhones([mapFirestoreToLegacy(doc)])[0]);

/** Contract rows as the page's open rows (a name and a pick-up date; open status; contacts phone added). */
const openRowsOf = (rows) => withContactPhones((rows || []).map(mapFirestoreToLegacy).filter((d) => {
    const name = d.customer || d['Customer'];
    const start = d.pickupDate || d['Pick-up Date'];
    return name && String(name).trim() && start && String(start).trim();
}).filter(isOpenStatus));

export function useDashboardLogic() {
    // Data State
    const [openContracts, setOpenContracts] = useState([]);
    const [closedContracts, setClosedContracts] = useState([]);
    // the contract numbers the server's search matched (new store): shown as they are - the server also finds a
    // misspelt name ("Abdulrahmen"), which the page's own exact text match would drop again
    const [serverHits, setServerHits] = useState(() => new Set());
    const [maintenanceData, setMaintenanceData] = useState([]);
    const [carsData, setCarsData] = useState([]);
    // The maintenance log and the fleet have been read (the rows show before them): the tabs are counted only from
    // then on - counted from the empty lists, Mismatch / Expired Cars would show a wrong 0 until they land
    const [logsAndFleetIn, setLogsAndFleetIn] = useState(false);
    const [invalidContracts] = useState([]);

    // Worker State Results: the tab lists, and whether open rows have been counted at all yet (statsPending)
    const [derived, setDerived] = useState({ stats: NO_WORKER_LISTS, counted: false });
    const derivedStats = derived.stats;

    // UI State
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastSync, setLastSync] = useState(null);
    const [isSyncing, setIsSyncing] = useState(false);
    const [searching, setSearching] = useState(false);   // the new store: a search is being fetched

    // Filter/Search State
    const [filterMode, setFilterMode] = useState(FILTER_MODES.ALL);
    const [contractTypeFilter, setContractTypeFilter] = useState('all');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

    const workerRef = useRef(null);
    // What was sent to the worker last: its number and the open rows it holds. An answer to an older send is dropped -
    // the newer one is on its way, and each answer taken draws the page again.
    const posted = useRef({ seq: 0, rows: null });
    // The new store: which load owns the open list - a later load's (a Refresh's) rows are never replaced by an
    // earlier load's background changes arriving late
    const loadSeq = useRef(0);

    // Initialize Worker
    useEffect(() => {
        workerRef.current = new Worker(new URL('./workers/contractsWorker.js', import.meta.url));

        workerRef.current.onmessage = (e) => {
            const { type, payload, seq } = e.data;
            if (type === 'PROCESS_COMPLETE' && seq === posted.current.seq) {
                setDerived({ stats: payload, counted: posted.current.rows.length > 0 });
            }
        };

        return () => {
            if (workerRef.current) workerRef.current.terminate();
        };
    }, []);

    // --- 1. Load Data (Cache First then DB) ---
    // `contractsOnly`: a Fill from Ejar changes contracts only - the maintenance log and the fleet come
    // from their saved copies instead of two more forced whole reads (CON-19).
    const loadData = useCallback(async (forceRefresh = false, { contractsOnly = false } = {}) => {
        try {
            const user = auth.currentUser;
            if (!user) throw new Error('Sign in first to see the data');

            if (!forceRefresh) setLoading(true);
            else setIsSyncing(true);
            // The new store: the open rows that arrive after this load has shown its own - newer, so they win
            // over the list this load read first, unless a later load (a Refresh) has started since. Only the
            // open list: the closed rows are the current search's and stay.
            const seq = ++loadSeq.current;
            let updated = false;
            const showNewer = (rows) => {
                if (seq !== loadSeq.current) return;
                updated = true;
                const open = openRowsOf(rows);
                setOpenContracts(open);
                // the next visit shows these first, not the older ones (a failed save only costs that)
                Promise.resolve(dbService.save('contracts_open', open)).catch((e) => logger.warn('[contracts v2] saving the open rows failed', e));
            };
            await contactsService.ensureLoaded();

            // 1. Try loading from cache (IndexedDB) first if not a force refresh
            if (!forceRefresh) {
                const [cachedOpenRaw, cachedClosedRaw, cachedMaintenance, cachedFleet] = await Promise.all([
                    dbService.load('contracts_open'),
                    dbService.load('contracts_closed'),
                    dbService.load('maintenance'),
                    dbService.load('fleet')
                ]);
                // The saved copy's balances were counted on the day it was saved: count them again for today
                // (the same helper as the worker's Unpaid tab, so the column and the tab agree - CON-24).
                const cachedOpen = withContactPhones(cachedOpenRaw).map((row) => ({ ...row, netBalance: liveBalance(row) }));
                // the new store loads the open contracts only; closed ones come by search
                const cachedClosed = USE_CONTRACTS_V2 ? [] : withContactPhones(cachedClosedRaw);

                if (cachedOpen && cachedOpen.length > 0) {
                    setOpenContracts(cachedOpen);
                    // the new store: the closed rows are the search's (a search that landed first stays)
                    if (!USE_CONTRACTS_V2) setClosedContracts(cachedClosed || []);
                    setMaintenanceData(cachedMaintenance || []);
                    setCarsData(cachedFleet || []);
                    setLogsAndFleetIn(true);
                    setLoading(false);
                    // The new store: this copy stays as it was saved until the changes since are read, so they are
                    // still read, in the background (review, 27 Sep 2026: they came only on Refresh). getOpenContracts
                    // gives its own saved copy at once - the delta then comes through showNewer - or, with no copy
                    // of its own, the one whole read of the open list; the rows on screen stay until either lands.
                    if (USE_CONTRACTS_V2) {
                        contractsService.getOpenContracts({ onUpdate: showNewer })
                            .then((rows) => { if (!updated) showNewer(rows); })
                            .catch((e) => logger.warn('[contracts v2] background read failed - the saved copy stays on screen', e));
                    }
                    return; // Early return if cache is loaded (the worker runs from the effect below)
                }
            }

            // 2. Fetch from Firestore (DB)
            // The new store (owner, 27 Sep 2026): the OPEN contracts only - a closed contract, a phone or a
            // customer is fetched by itself when searched (the search effect below), not 5 years of contracts.
            // The maintenance log and the fleet are read alongside, but the rows do not wait for them (review, 28 Sep
            // 2026): each is its own network read on every visit (a whole one once a day, or in a new browser), and
            // they only mark the rows and feed the tab counts, which fill in when they land. `side` is set as soon as
            // both have ended.
            let side = null;
            const sideRead = Promise.all([
                maintenanceService.getAllLogs(forceRefresh && !contractsOnly),
                fleetService.getAllCars(forceRefresh && !contractsOnly)
            ]).then((lists) => { side = { lists }; return side; }, (failure) => { side = { failure }; return side; });
            const allDocs = await (USE_CONTRACTS_V2
                // the browser's copy at once; the contracts changed since arrive in the background
                ? contractsService.getOpenContracts({ force: forceRefresh, onUpdate: showNewer })
                : contractsService.getAllContracts(forceRefresh));

            // Map and identify Open/Closed
            const mappedData = allDocs.map(mapFirestoreToLegacy);
            const validData = mappedData.filter(d => {
                const hasName = (d.customer || d['Customer']) && (d.customer || d['Customer']).toString().trim().length > 0;
                const hasStart = (d.pickupDate || d['Pick-up Date']) && (d.pickupDate || d['Pick-up Date']).toString().trim().length > 0;
                return hasName && hasStart;
            });

            const open = withContactPhones(validData.filter(isOpenStatus));
            const closed = withContactPhones(validData.filter(d => !isOpenStatus(d)));

            // Save to State (the new store: the closed rows are the current search's - a refresh keeps them). The new
            // store: the changes since may already have landed (showNewer) - then `open` (the saved copy) is older than
            // the list on screen and must not replace it.
            if (!(USE_CONTRACTS_V2 && updated)) setOpenContracts(open);
            if (!USE_CONTRACTS_V2) setClosedContracts(closed);
            // the rows show now: the maintenance log and the fleet, and the browser's saved copy below, are not a wait
            // for them (owner, 28 Sep 2026)
            setLoading(false);

            // Read already: no wait here, so the rows and their marks draw together (one draw of the table, not two)
            const { lists, failure } = side || await sideRead;
            if (failure) throw failure;
            const [maintenanceLogs, fleetDocs] = lists;
            setMaintenanceData(maintenanceLogs);
            setCarsData(fleetDocs);
            setLogsAndFleetIn(true);

            const now = new Date().toISOString();
            setLastSync(now);

            // The new store: the changes since may land while the maintenance log and the fleet are still read -
            // then `open` (the saved copy) is older than the list on screen, and than the copy showNewer saved
            const newerShown = USE_CONTRACTS_V2 && updated;

            // 3. Save to Cache (IndexedDB)
            await Promise.all([
                newerShown ? null : dbService.save('contracts_open', open),
                dbService.save('contracts_closed', closed),
                dbService.save('maintenance', maintenanceLogs),
                dbService.save('fleet', fleetDocs)
            ]);

            // 5. AUTO-SYNC (BACKGROUND): Update Firestore for any contracts with missing rows
            if (forceRefresh) {
                setTimeout(async () => {
                    try {
                        const allOpenRaw = allDocs.filter(isOpenStatus);
                        if (USE_CONTRACTS_V2) {
                            // the contracts store: each contract's months read from it, the missing ones saved there
                            const done = await syncOpenMonthsToStore(allOpenRaw, { log: logger });
                            if (done.saved > 0) logger.log(`[Auto-Sync] Saved missing rows for ${done.saved} contracts (contracts store).`);
                            return;
                        }
                        const updates = syncMissingRowsForOpenContracts(allOpenRaw);
                        if (updates.length > 0) {
                            const updatedBy = (auth.currentUser?.displayName || 'Auto-Sync');
                            for (const u of updates) {
                                try {
                                    await contractsService.updateContract(u.id, u.data, updatedBy, 'Auto-Sync', { expectedLastUpdated: u.lastUpdated });
                                } catch (e) {
                                    // changed since it was read: its own rows win; the next load generates again
                                    if (e.code !== CONTRACT_CHANGED) throw e;
                                    logger.info(`[Auto-Sync] ${u.id} changed since it was read - not overwritten`);
                                }
                                await new Promise(r => setTimeout(r, 300));
                            }
                            logger.log(`🔍 [Auto-Sync] Saved missing rows for ${updates.length} contracts.`);
                        }
                    } catch (syncErr) {
                        logger.error('Auto-sync failed', syncErr);
                    }
                }, 5000); // Wait 5s to not compete with main sync
            }

        } catch (err) {
            logger.error('Failed to load dashboard data', err);
            setError(err);
        } finally {
            setLoading(false);
            setIsSyncing(false);
        }
    }, []);

    useEffect(() => {
        loadData();
    }, [loadData]);

    // The new store: what is searched is fetched from the server (open or closed, any year) and held as the
    // 'closed' rows until the search changes; the open ones are already loaded. A newer search wins a race.
    const searchSeq = useRef(0);
    useEffect(() => {
        if (!USE_CONTRACTS_V2) return undefined;
        const term = debouncedSearchTerm.trim();
        const seq = ++searchSeq.current;
        if (term.length < 3) { setClosedContracts([]); setServerHits(new Set()); setSearching(false); return undefined; }
        setSearching(true);
        contractsService.findMonthlyContracts(term)
            .then((docs) => {
                if (seq !== searchSeq.current) return;
                const all = docs.map(mapFirestoreToLegacy);
                setServerHits(new Set(all.map((d) => String(d.contractNo || '').trim().toUpperCase()).filter(Boolean)));
                setClosedContracts(withContactPhones(all.filter((d) => !isOpenStatus(d))));
            })
            .catch((err) => { if (seq === searchSeq.current) logger.error('Contract search failed', err); })
            .finally(() => { if (seq === searchSeq.current) setSearching(false); });
        return undefined;
    }, [debouncedSearchTerm]);

    const refreshData = useCallback(() => loadData(true), [loadData]);
    const refreshContracts = useCallback(() => loadData(true, { contractsOnly: true }), [loadData]);

    // The filter tabs (Mismatch, Unpaid, No Rent ...) show the worker's rows. It re-runs whenever the rows
    // change - a load, a calculator save, an Edit Mode save, a delete - not only on a load: a contract
    // saved from the 'No Rent' tab used to stay there with its old balance until a reload (CON-12).
    // Not before the maintenance log and the fleet are in (the rows show first): counted without them, the tabs
    // would give a wrong answer for a moment (review, 28 Sep 2026).
    useEffect(() => {
        if (!workerRef.current || !logsAndFleetIn) return;
        const seq = posted.current.seq + 1;
        posted.current = { seq, rows: openContracts };
        workerRef.current.postMessage({
            type: 'PROCESS_DATA',
            seq,
            payload: { openContracts, maintenanceData: maintenanceData || [], carsData: carsData || [] }
        });
    }, [openContracts, maintenanceData, carsData, logsAndFleetIn]);
    // The open rows were never counted yet (the first load, until the worker's first answer for them): a worker tab
    // with no row says 'Counting…', never "No contracts found", and the tab badges show no number, not a wrong 0 (the
    // tabs are on screen during the load too). A recount (a save, the background update, a Refresh) keeps the last
    // answer on screen until the new one lands - no 'Counting…' flash over an empty tab already counted (review,
    // 28 Sep 2026).
    const statsPending = !derived.counted && (loading || openContracts.length > 0);

    // --- 3. Local Updates ---
    const updateLocalData = useCallback((updatedItems) => {
        const updater = (prev) => {
            const next = [...prev];
            updatedItems.forEach(updated => {
                const idx = next.findIndex(d => d.id === updated.id || d.contractNo === updated.contractNo);
                if (idx !== -1) next[idx] = { ...next[idx], ...updated };
            });
            return next;
        };
        setOpenContracts(prev => updater(prev));
        setClosedContracts(prev => updater(prev));
    }, []);

    const removeLocalData = useCallback((contractId) => {
        setOpenContracts(prev => prev.filter(c => c.id !== contractId));
        setClosedContracts(prev => prev.filter(c => c.id !== contractId));
    }, []);

    // --- 4. Filtering (Main Thread Memo for responsiveness) ---
    // The rows of the chosen tab: 'All Contracts' = the open rows as soon as they are loaded; a worker tab = its list
    // when the worker answers. Only that list is a dependency below - a tab count arriving does not rebuild (and
    // redraw) the 'All Contracts' rows (owner, 28 Sep 2026).
    const workerList = WORKER_LIST_OF_MODE[filterMode];
    const tabRows = workerList ? derivedStats[workerList] : openContracts;
    const filteredData = useMemo(() => {
        let data = tabRows;

        // 0. Search Priority (Main Thread Sync)
        if (debouncedSearchTerm.trim()) {
            const term = normalizeForSearch(debouncedSearchTerm);
            const termDate = parseSheetDate(debouncedSearchTerm);
            
            const isSameDay = (d1, d2) => 
                d1.getFullYear() === d2.getFullYear() && 
                d1.getMonth() === d2.getMonth() && 
                d1.getDate() === d2.getDate();

            // Search across ALL contracts (Open + Closed) if searching
            // Or just search within the current view? Usually global search is better.
            // User expects "Find Contract 123" to find it even if closed.
            const source = [...openContracts, ...closedContracts];

            data = source.filter(row => {
                if (serverHits.has(String(row.contractNo || '').trim().toUpperCase())) return true;
                if (termDate) {
                    const dDate = parseSheetDate(row.dropoffDate);
                    const pDate = parseSheetDate(row.pickupDate);
                    
                    if (filterMode === FILTER_MODES.BOOKING_EXP) {
                        // Strict match only on dropoffDate
                        if (dDate && isSameDay(dDate, termDate)) return true;
                        return false;
                    } else {
                        // General search matches both
                        if (dDate && isSameDay(dDate, termDate)) return true;
                        if (pDate && isSameDay(pDate, termDate)) return true;
                    }
                }

                const cNo = normalizeForSearch(row.contractNo);
                const phone = normalizeForSearch(row.phoneNumber);
                const invPlate = normalizeForSearch(row.invygoPlate);
                const ejarPlate = normalizeForSearch(row.ejarPlate);
                const customer = normalizeForSearch(row.customer);
                const booking = normalizeForSearch(row.bookingNumber);
                const pickup = normalizeForSearch(row.pickupDate);
                const dropoff = normalizeForSearch(row.dropoffDate);

                return cNo.includes(term) ||
                    phone.includes(term) ||
                    phoneMatches(debouncedSearchTerm, row) ||
                    identityMatches(term, row) ||
                    invPlate.includes(term) ||
                    ejarPlate.includes(term) ||
                    customer.includes(term) ||
                    booking.includes(term) ||
                    pickup.includes(term) ||
                    dropoff.includes(term);
            });
        }

        if (contractTypeFilter !== 'all') {
            data = data.filter(row => matchesContractType(row, contractTypeFilter));
        }

        return [...data].sort((a, b) => {
            if (filterMode === FILTER_MODES.UNPAID) {
                const balA = parseFloat(a.netBalance) || 0;
                const balB = parseFloat(b.netBalance) || 0;
                if (balA !== balB) return balB - balA; // Descending by highest balance
            }
            const dateA = parseSheetDate(a.pickupDate);
            const dateB = parseSheetDate(b.pickupDate);
            return (dateB ? dateB.getTime() : 0) - (dateA ? dateA.getTime() : 0);
        });
    }, [filterMode, contractTypeFilter, tabRows, openContracts, closedContracts, debouncedSearchTerm, serverHits]);

    // While searching, the Open / Closed tables are the SAME rows as filteredData (the Export's rows),
    // split by status. They were a second search that ignored the contract-type filter, so with
    // 'Monthly' selected the screen showed every match and the export only the monthly ones (CON-17).
    const searchResults = useMemo(
        () => (debouncedSearchTerm.trim() ? splitSearchResults(filteredData) : splitSearchResults([])),
        [filteredData, debouncedSearchTerm]
    );

    // Cell-level marks (the tabs count the same in the worker): the car's expired registration / insurance, and WHAT
    // expired and when - the table shows it next to the plate, so "Exp" is never a mystery ("insurance 18/09/2026").
    // The fleet is indexed by plate once per list (utils/carExpiry): each plate cell used to scan every car - ~1.7 s
    // of the first render (owner, 28 Sep 2026).
    const fleetMarks = useMemo(() => fleetExpiry(carsData), [carsData]);
    // Switch Back for the table's marks and its WhatsApp menus: the tabs' own rule and lookups (utils/switchBack) -
    // {via, since, days} or null; the Dubai day is read on every call, so it turns at midnight
    const switchLookups = useMemo(() => switchBackLookups(maintenanceData, carsData), [maintenanceData, carsData]);
    const switchBackOfRow = useCallback((row) => (row && row.type === 'open'
        ? switchBackOf(row, { ...switchLookups, today: dubaiToday() }) : null), [switchLookups]);
    // ... and the Mismatch tab's own test of a row (its own car not Rented elsewhere / gone)
    const inMismatchOfRow = useCallback((row) => !!row && row.type === 'open' && inMismatch(row, switchLookups), [switchLookups]);

    // The Booking Expired badge counts only the returns whose drop-off has already passed (the tab itself is a
    // returns calendar of every open contract with a drop-off date) - the Dubai calendar day, as the table's
    // 'Booking expired' stripe (CON-24); read on every draw, so it turns at midnight.
    const todayMs = dubaiToday().getTime();
    const bookingExpiredCount = derivedStats.bookingExp.filter((row) => {
        const d = parseSheetDate(row.dropoffDate);
        return !!d && d.getTime() < todayMs;
    }).length;
    // A worker tab's badge: no number while the rows on screen were never counted (the tab fills in), not a 0 that
    // says the tab is empty (review, 28 Sep 2026)
    const countOf = (n) => (statsPending ? null : n);



    // ... inside useDashboardLogic ...

    return {
        loading, error,
        filterMode, setFilterMode,
        contractTypeFilter, setContractTypeFilter,
        debouncedSearchTerm, setDebouncedSearchTerm,
        filteredData,
        invalidContracts, // Export this

        duplicatedContractsInfo: NO_DUPLICATES, // Simplified for now, can be moved to worker too
        maintenanceData,
        isCarExpired: fleetMarks.isCarExpired,
        getExpiryDate: fleetMarks.getExpiryDate,
        switchBackOf: switchBackOfRow,
        inMismatchOf: inMismatchOfRow,
        statsPending,
        stats: {
            mismatch: countOf(derivedStats.mismatch.length),
            switchback: countOf(derivedStats.switchback.length),
            expired: countOf(derivedStats.expired.length),
            bookingExp: countOf(bookingExpiredCount),
            latePayment: countOf(derivedStats.latePayment.length),
            staleKm: countOf(derivedStats.staleKm.length),
            noRent: countOf(derivedStats.noRent.length),
            unpaid: countOf(derivedStats.unpaid.length),
            // the 'All Contracts' badge counts what its table lists (the contract-type filter applies)
            // (no number while the first load runs: not a 0 before the rows are in)
            open: loading ? null : (contractTypeFilter === 'all' ? openContracts.length : openContracts.filter((row) => matchesContractType(row, contractTypeFilter)).length)
        },
        searchResults,
        refreshData, refreshContracts, updateLocalData, removeLocalData, isSyncing, lastSync, searching,
        openContracts, closedContracts
    };
}
